import * as db from '@debbie/common/dist/query/db.js'
import {
  EntityName,
  FilterX,
  Property,
  Query,
  and,
  query,
  toTuple,
} from '@debbie/common/dist/query/index.js'
import { search } from '@debbie/common/dist/util/index.js'
import {
  AugmentedFilter,
  ViewDescription,
} from '@debbie/common/dist/views/index.js'
import { c } from '@debbie/cortex/dist/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { FilterMenuV2 } from '@debbie/iris/dist/components/FilterV2/FilterMenuV2.js'
import { Button, CheckboxSimple } from '@debbie/iris/dist/components/index.js'
import THEME from '@debbie/iris/dist/theme.js'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'

import { genProperties, toFilters } from './utils.js'
import { DisplayOptions, View } from './View.js'
import { baseContext, displays, views } from './views.js'

type Props = {
  viewDescription: ViewDescription
  onSave: (viewDescription: ViewDescription) => void
  onAutoSave: (viewDescription: ViewDescription) => void
  resetLocal: () => void
  updating: boolean
  localChanges: boolean
}

type View<Base extends EntityName, Q extends Query<typeof db.schema, Base>> = {
  query: Q
  displayOptions: DisplayOptions<Base, Q>
}

export const ViewPage = (props: Props) => {
  const lang = useSelector((s: RootState) => s.lang)

  const [base, setBase] = useState<EntityName>(
    props.viewDescription?.base ?? 'cases',
  )
  const [filters, setFilters] = useState<AugmentedFilter[]>(
    props.viewDescription ? props.viewDescription.filters : [],
  )
  const [displayOptions, setDisplayOptions] = useState<string[]>(
    props.viewDescription ? props.viewDescription.displayOptions : ['cases.id'],
  )
  const [orderBy, setOrderBy] = useState(
    props.viewDescription?.orderBy ?? 'createdAt',
  )
  const [orderByDirection, setOrderByDirection] = useState(
    props.viewDescription?.orderByDirection ?? 'DESC',
  )

  const [draggingIdx, setDraggingIdx] = useState<number | null>()

  const [dropdownVisible, setDropdownVisible] = useState(false)
  const [searchText, setSearchText] = useState('')

  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownVisible(false)
      }
    }

    if (dropdownVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownVisible])

  const [initial, setInitial] = useState(true)
  const [justSaved, setJustSaved] = useState(false)
  useEffect(() => {
    if (
      !initial &&
      props.viewDescription &&
      !props.localChanges &&
      !justSaved
    ) {
      setBase(props.viewDescription.base)
      setFilters(props.viewDescription.filters)
      setDisplayOptions(props.viewDescription.displayOptions)
      setOrderBy(props.viewDescription.orderBy)
      setOrderByDirection(props.viewDescription.orderByDirection ?? 'ASC')
    }
    if (justSaved) {
      setJustSaved(false)
    }
  }, [props.localChanges])

  useEffect(() => {
    if (initial) {
      setInitial(false)
    } else if (
      props.onAutoSave &&
      (base != props.viewDescription.base ||
        filters != props.viewDescription.filters ||
        displayOptions != props.viewDescription.displayOptions ||
        orderBy != props.viewDescription.orderBy ||
        orderByDirection != props.viewDescription.orderByDirection)
    ) {
      props.onAutoSave({
        base,
        filters,
        displayOptions,
        orderBy,
        orderByDirection,
      })
    }
  }, [base, filters, displayOptions, orderBy, orderByDirection])

  // @ts-expect-error
  const viewList = displayOptions.map((option) => views[option])

  const ctx = baseContext[props.viewDescription.base]

  const { properties, scopedProperties } = genProperties(
    base,
    displayOptions,
    ctx?.preview,
  )

  const possibleViews = (baseContext[base]?.availableViews ?? []).map(
    (v) => views[v],
  )

  const q = query(base)({
    base: base,
    properties: properties,
    scoped: scopedProperties,
    // @ts-expect-error
    orderBy: orderBy,
    orderByDirection: orderByDirection,
    offset: 0,
    limit: 100,
    filters: toFilters(filters),
  })

  const options: DisplayOptions<typeof q.base, typeof q> = {
    view: displayOptions.map((option) => {
      const viewOption = views[option as keyof typeof views]

      if ('customDisplay' in viewOption) {
        return {
          ...viewOption,
          component: displays[viewOption.customDisplay],
        }
      }
      return viewOption
    }),
  }

  return (
    <>
      <TopWrapper>
        <FilterMenuV2 base={base} filters={filters} setFilters={setFilters} />
        <ButtonWrapper>
          <Button
            onClick={() => {
              setDropdownVisible((visible) => !visible)
            }}
            theme="secondary"
          >
            {lang.views.displayOptions}
          </Button>
          {dropdownVisible ? (
            <Dropdown ref={dropdownRef}>
              <SearchInput
                value={searchText}
                onChange={(e) => setSearchText(e.currentTarget.value)}
                placeholder={`${lang.misc.search}...`}
                autoFocus
              ></SearchInput>
              <DropdownItems>
                {(searchText === ''
                  ? [
                      ...viewList,
                      ...possibleViews.filter(
                        (x) =>
                          viewList.find((l) => l.value === x.value) == null,
                      ),
                    ]
                  : search(possibleViews, (v) => v.label(lang), searchText, {})
                ).map((v, i) => {
                  return (
                    <DropdownItem
                      key={v.value}
                      draggable={
                        searchText == '' &&
                        viewList.find((item) => item.value === v.value) != null
                      }
                      onDragStart={(e) => {
                        setDraggingIdx(i)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDrop={(e) => {
                        e.preventDefault()
                        if (draggingIdx != null) {
                          setDisplayOptions((options) => {
                            const val = options[draggingIdx]

                            const arr = [
                              ...options.slice(0, draggingIdx),
                              ...options.slice(draggingIdx + 1),
                            ]

                            return [...arr.slice(0, i), val, ...arr.slice(i)]
                          })
                        }
                      }}
                      onDragEnd={(e) => {
                        setDraggingIdx(null)
                      }}
                      onDragOver={(e) => {
                        e.preventDefault()
                        return false
                      }}
                    >
                      <CheckboxSimple
                        checked={
                          viewList.find((item) => item.value === v.value) !=
                          null
                        }
                        onChange={(e) => {
                          const idx = viewList.findIndex(
                            (item) => item.value === v.value,
                          )
                          if (idx === -1) {
                            setDisplayOptions((options) => [
                              ...options,
                              v.value,
                            ])
                          } else {
                            setDisplayOptions((options) => [
                              ...options.slice(0, idx),
                              ...options.slice(idx + 1),
                            ])
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {v.label(lang)}
                    </DropdownItem>
                  )
                })}
              </DropdownItems>
            </Dropdown>
          ) : null}
        </ButtonWrapper>
        <div>
          <Button
            loading={props.updating}
            disabled={props.updating || !props.localChanges}
            style={{ flexShrink: '0' }}
            onClick={() => {
              if (props.onSave) {
                props.onSave({
                  base,
                  filters,
                  displayOptions,
                  orderBy,
                  orderByDirection,
                })
                setJustSaved(true)
              }
            }}
          >
            {lang.misc.save}
          </Button>
        </div>
      </TopWrapper>

      {props.localChanges ? (
        <LocalChanges>
          <span>{lang.views.localChanges}</span>
          {' - '}
          <Reset onClick={props.resetLocal}>
            {lang.views.resetLocalChanges}
          </Reset>
        </LocalChanges>
      ) : (
        <LocalChanges>
          <span> </span>
        </LocalChanges>
      )}
      <View<typeof q.base, typeof q>
        base={q.base}
        query={q}
        options={options}
        onSortChange={(sort) => {
          setOrderBy(sort.key ?? q.orderBy)
          setOrderByDirection(sort.direction ?? q.orderByDirection ?? 'ASC')
        }}
        onClick={
          baseContext[base] != null
            ? (x) => {
                const [table, prop] = toTuple(baseContext[base]!.itemId)

                // @ts-expect-error
                const id = x[base] != null ? x[table][prop] : undefined

                if (id != null) {
                  c.redirect.redirect(`${window.location.pathname}/queue/${id}`)
                }
              }
            : undefined
        }
      />
    </>
  )
}

const TopWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
`

const Dropdown = styled.div`
  right: 0px;
  position: absolute;
  background-color: #fefefe;
  border-radius: 4px;
  border: 1px solid lightgray;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1;
`

const ButtonWrapper = styled.div`
  display: inline-block;
  user-select: none;
  position: relative;
`

const DropdownItems = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  flex-direction: column;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  scrollbar-width: none;
`

const SearchInput = styled.input`
  padding: 8px 16px;
  font-size: 1em;
  outline: none;
  border: none;
  background: none;
  border-bottom: 1px solid lightgray;
  width: 100%;
`

const DropdownItem = styled.div`
  transition: background-color 50ms ease-in-out;

  padding: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;

  &:hover {
    background-color: ${THEME.FILL};
  }
`

const LocalChanges = styled.div`
  padding: 0.2em;
  border-radius: 4px;
  margin-bottom: 1em;
  font-size: 0.8em;
  height: 1em;
`

const Reset = styled.span`
  font-weight: bold;
  cursor: pointer;
`
